// SharePoint sites under their credential holder on the Connections pages
// (PLAN-20260930 slice 9; challenge record
// 2026-10-03-connections-list-nests-sharepoint-sites). governance-service
// decides which connections are sites of a holder (ADR-20261002); this module
// only asks for that answer and reads it as served. Pickers never ask, so
// they keep listing every site.
import { api } from '@/api'
import type { GovernanceResource } from './governanceResource.types'
import { INCLUDE_SETUP } from './connectionSetupStatus'

/** The one nest value governance accepts; the response echoes it under `nest`. */
export const NEST_CREDENTIAL_SITES = 'credential_sites'

/** The query parameter a Connections page adds to its list read. */
export const NEST_PARAM = { nest: NEST_CREDENTIAL_SITES } as const

export interface CredentialSiteMatch {
  id: string
  name: string
  displayName: string
}

/** credential_sites as served on a holder row: its site count and, under a search, the matching sites. */
export interface CredentialSitesSummary {
  count: number
  matched: CredentialSiteMatch[]
}

/** Whether a response says the server nested its rows. Without the echo nothing is shown as nested. */
export function nestApplied(page: { nest?: unknown } | null | undefined): boolean {
  return page?.nest === NEST_CREDENTIAL_SITES
}

/** A row as shown when the server did not nest: no site count, never a guessed one. */
export function withoutCredentialSites<T extends { credentialSites?: unknown }>(row: T): T {
  if (!('credentialSites' in row)) return row
  const { credentialSites: _dropped, ...rest } = row
  return rest as T
}

const text = (value: unknown): string => (typeof value === 'string' ? value : '')

function servedMatch(raw: unknown): CredentialSiteMatch | null {
  if (!raw || typeof raw !== 'object') return null
  const record = raw as Record<string, unknown>
  const id = text(record.id)
  if (!id) return null
  return { id, name: text(record.name), displayName: text(record.displayName ?? record.display_name) }
}

/**
 * credential_sites of one served row, in either key case; undefined when the
 * row carries none or a malformed one, so a page shows nothing rather than a
 * zero the server never sent.
 */
export function servedCredentialSites(row: unknown): CredentialSitesSummary | undefined {
  if (!row || typeof row !== 'object') return undefined
  const record = row as Record<string, unknown>
  const raw = record.credentialSites ?? record.credential_sites
  if (!raw || typeof raw !== 'object') return undefined
  const { count, matched } = raw as { count?: unknown; matched?: unknown }
  if (typeof count !== 'number' || !Number.isSafeInteger(count) || count < 0) return undefined
  const sites = Array.isArray(matched) ? matched.map(servedMatch).filter((site): site is CredentialSiteMatch => site !== null) : []
  return { count, matched: sites }
}

/**
 * The sites borrowing a holder's credential, as governance counts them for
 * the Connections list (GET /resources/{id}/children?nest=credential_sites).
 * A response without the echo comes from a server that did not classify the
 * children, so the read fails and the page offers a retry instead of guessing.
 */
export async function listCredentialSites(holderId: string): Promise<GovernanceResource[]> {
  const data = await api.get<{ children?: GovernanceResource[]; nest?: string }>(
    `governance/resources/${encodeURIComponent(holderId)}/children`,
    // Each site's setup status comes with it (Agion#2673): no per-host reads.
    { ...NEST_PARAM, ...INCLUDE_SETUP },
  )
  if (!nestApplied(data) || !Array.isArray(data.children)) {
    throw new Error('Governance did not say which connections use this credential')
  }
  return data.children
}
